// angular imports
import {
    Component,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';

// 3rd party imports
import { Observable } from 'rxjs';

// CC-Common imports
import { Utils } from 'cc-common';

// devextreme imports
import { DxDataGridComponent } from 'devextreme-angular';
import * as events from 'devextreme/events';
import CheckBox from 'devextreme/ui/check_box';

// component imports
import { BoardApprovalBaseComponent } from '../../_base/board-approval-base.component';

// Service imports
import { SharedAccApplicationService } from '../../../shared-acc-board-approval-module/_services/shared-acc-application.service';
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';

// constant imports
import { BoardApprovalProcessConstants } from '../../../shared-acc-board-approval-module/_constants/board-approval-process-constants';

// model imports
import { FileInfoDisplayResultDto } from '../../../_models/file-info-display-result-dto';
import { AccBaseParameterDto } from '../../../shared-acc-board-approval-module/_models/acc-base-parameter-dto';
import { AccDocumentDetailParametersDto } from '../../../shared-acc-board-approval-module/_models/acc-document-detail-parameters-dto';
import { AccHistoryResultDto } from '../../../shared-acc-board-approval-module/_models/acc-history-result-dto';
import { BoardApprovalBaseParameterDto } from '../../../shared-acc-board-approval-module/_models/board-approval-base-parameter-dto';
import { BoardApprovalCollectionReferralResultDto } from '../../../shared-acc-board-approval-module/_models/board-approval-collection-referral-result-dto';
import { BoardApprovalDetailParameterDto } from '../../../shared-acc-board-approval-module/_models/board-approval-detail-parameter-dto';
import { BoardApprovalItemResultDto } from '../../../shared-acc-board-approval-module/_models/board-approval-item-result-dto';
import { BoardApprovalProcessesResultDto } from '../../../shared-acc-board-approval-module/_models/board-approval-processes-result-dto';
import { BoardApprovalQueueParameterDto } from '../../../shared-acc-board-approval-module/_models/board-approval-queue-parameter-dto';
import { BoardApprovalRulesResultDto } from '../../../shared-acc-board-approval-module/_models/board-approval-rules-result-dto';
import { BoardApprovalWFParameterDto } from '../../../shared-acc-board-approval-module/_models/board-approval-wf-parameter-dto';
import { BoardApprovalQueueResultDto } from '../../_models/board-approval-queue-result-dto';

@Component({
    selector: 'app-board-approval-grid',
    templateUrl: './board-approval-grid.component.html',
    styleUrls: ['./board-approval-grid.component.scss']
})
export class BoardApprovalGridComponent extends BoardApprovalBaseComponent implements OnInit {
    @ViewChild('boardApprovalGrid', { static: false }) boardApprovalGrid: DxDataGridComponent;

    @Input() queueParams: BoardApprovalQueueParameterDto;
    @Input() mode: string;

    @Output() onRowSelection = new EventEmitter<BoardApprovalItemResultDto[]>();

    gridSource: Array<BoardApprovalItemResultDto> = [];
    selectedRequest: BoardApprovalItemResultDto;

    showEditor = false;
    showGridNotes = false;
    showCollectionReferral = false;
    showAccDetail = false;
    showAccEditor = false;
    showWorkflowViewer = false;
    showAccAttachments = false;
    showAccTransactionLog = false;
    showCollectionTransactionLog = false;
    showWorkFlowTransactionLog = false;
    showCollectionAttachment = false;
    showBoardApprovalPopup = false;

    storageKey: string;
    collectionsDetail: BoardApprovalCollectionReferralResultDto;

    boardApprovalParam: any = {};
    accEditorTitle: string;
    popupTitle: string;
    transactionLogResultData: any[];
    collectionLogUrl: string;
    collectionDocumentsUrl: string;
    boardApprovalRules: string;

    workFlowAttachment: FileInfoDisplayResultDto;
    showWorkflowAttachment = false;
    isRowsSelectable = true;

    boardApprovalQueueLoadingMessage: string;

    constructor(
        protected readonly injector: Injector,
        private readonly accApplicationService: SharedAccApplicationService,
        private readonly contextManagerService: ContextManagerService
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.setBoardApprovalQueueLoadingMessage();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.setBoardApprovalQueueLoadingMessage();

        if (Utils.notNullAndDefined(changes.mode) && Utils.notNullAndDefined(changes.mode.currentValue)) {
            this.mode = changes.mode.currentValue;
            this.storageKey = `${this.mode}_BOARD_APPROVAL`;
        }
        if (Utils.notNullAndDefined(changes.queueParams) && Utils.notNullAndDefined(changes.queueParams.currentValue)) {
            this.queueParams = changes.queueParams.currentValue;
            this.populateBoardApprovalQueue();
        }
    }

    getGroupTemplateTitle(processCode: string): string {
        let processDisplayText: string;
        const process = this.getSelectedProcess(processCode);
        if (Utils.notNullAndDefined(process) && Utils.notNullAndDefined(process.processDisplayText)) {
            processDisplayText = process.processDisplayText;
        } else {
            processDisplayText = '';
        }
        return processDisplayText;
    }

    getCount(data: any): number {
        return Utils.notNullAndDefined(data) &&
            Utils.equals(data.rowType, 'group') &&
            Utils.notNullOrEmpty(data.summaryItems)
            ? data.data.aggregates[0]
            : 0;
    }

    getProcessIdGroupTemplateTitle(data: any): string {
        let groupTitleText: string = null;
        if (Utils.notNullAndDefined(data)) {
            if (Utils.notNullOrEmpty(data.items)) {
                const items =
                    Utils.notNullOrEmpty(data.items) &&
                    Utils.isInList('processIdDisplayText', Object.keys(data.items[0]))
                        ? data.items
                        : Utils.notNullOrEmpty(data.items[0].items)
                        ? data.items[0].items
                        : data.items[0].collapsedItems;
                if (Utils.notNullOrEmpty(items)) {
                    groupTitleText = `ID: ${items[0].processIdDisplayText}: ${items[0].description} (Count=${items.length})`;
                }
            } else if (Utils.notNullOrEmpty(data.collapsedItems)) {
                const items =
                    Utils.notNullOrEmpty(data.collapsedItems) &&
                    Utils.isInList('processIdDisplayText', Object.keys(data.collapsedItems[0]))
                        ? data.collapsedItems
                        : data.collapsedItems[0].items;
                if (Utils.notNullOrEmpty(items)) {
                    groupTitleText = `ID: ${items[0].processIdDisplayText}: ${items[0].description} (Count=${items.length})`;
                }
            }
            return groupTitleText;
        }
    }

    onApprovalRowSelection(event: any): void {
        setTimeout(() => {
            if (Utils.notNullOrEmpty(event.selectedRowsData)) {
                const selectedRecords = this.boardApprovalGrid.instance.getSelectedRowsData();
                if (Utils.equals(selectedRecords.length, 1)) {
                    const boardApprovalItem: BoardApprovalItemResultDto = selectedRecords[0];

                    // When first record is selected, set allowSelection to true for records of same community, process and status
                    this.gridSource.forEach((rec) => {
                        if (
                            Utils.equals(rec.communityId, boardApprovalItem.communityId) &&
                            Utils.equalsIgnoreCase(rec.process, boardApprovalItem.process) &&
                            Utils.equalsIgnoreCase(rec.status, boardApprovalItem.status)
                        ) {
                            rec.allowSelection = true;
                        } else {
                            rec.allowSelection = false;
                        }
                    });
                    this.boardApprovalGrid.instance.repaint();
                } else {
                    const rowsToDeSelect = [];
                    selectedRecords.forEach((rec) => {
                        if (Utils.isFalse(rec.allowAction)) {
                            rowsToDeSelect.push(rec);
                        }
                    });
                    this.boardApprovalGrid.instance.deselectRows(rowsToDeSelect);
                    this.boardApprovalGrid.instance.repaint();
                }
            } else {
                this.gridSource.forEach((rec) => {
                    rec.allowSelection = true;
                });

                this.boardApprovalGrid.instance.repaint();
            }

            this.onRowSelection.emit(this.boardApprovalGrid.instance.getSelectedRowsData());
        }, 100 /** Added some delay to avoid UI exception of ExpressionChangedAfterItHasBeenCheckedError */);
    }

    onCellPrepared(e: any): void {
        if (e.rowType === 'data' && e.column.command === 'select') {
            const checkboxElement = e.cellElement.querySelector('.dx-select-checkbox');
            const instance = CheckBox.getInstance(checkboxElement);
            if (
                Utils.isFalse(e.data.allowSelection) ||
                Utils.isNullOrEmpty(e.data.allowAction) ||
                Utils.isFalse(e.data.allowAction)
            ) {
                instance.option('visible', false);
                events.off(e.cellElement);
            }
        }
    }

    onGridContentReady(): void {
        const isRowSelectable = this.gridSource.find(
            (d) => Utils.isTrue(d.allowSelection) && Utils.notNullOrEmpty(d.allowAction) && Utils.isTrue(d.allowAction)
        );
        this.isRowsSelectable = Utils.notNullAndDefined(isRowSelectable);
    }

    onGridAttachmentClick(record: BoardApprovalItemResultDto): void {
        this.selectedRequest = record;
        if (Utils.equalsIgnoreCase(this.selectedRequest.process, BoardApprovalProcessConstants.ACC)) {
            const param: AccDocumentDetailParametersDto = new AccDocumentDetailParametersDto();
            param.communityId = this.selectedRequest.communityId;
            param.accItemId = Number(this.selectedRequest.processId);
            param.customerId = record.customerId;
            this.boardApprovalParam = param;
            this.showAccAttachments = true;
        } else if (Utils.equalsIgnoreCase(this.selectedRequest.process, BoardApprovalProcessConstants.WF)) {
            this.populateWorkflowDocuments();
        } else if (Utils.equalsIgnoreCase(this.selectedRequest.process, BoardApprovalProcessConstants.COLLECTIONS)) {
            this.popupTitle = `${record.allowAction === true ? 'Manage' : ''} Approval Documents for ${
                this.selectedRequest.customerId
            }`;
            this.showCollectionAttachment = true;
        }
    }

    onCollectionAttachmentClose(): void {
        this.showCollectionAttachment = false;
        this.populateBoardApprovalQueue();
    }

    onGridNotesClick(record: BoardApprovalItemResultDto): void {
        this.selectedRequest = record;
        this.showGridNotes = true;
    }

    onNotesEditorClose(forceReload: boolean): void {
        this.showGridNotes = false;
        if (Utils.isTrue(forceReload)) {
            this.populateBoardApprovalQueue();
        }
    }

    onGridHistoryClick(record: BoardApprovalItemResultDto): void {
        this.selectedRequest = record;
        switch (this.selectedRequest.process) {
            case BoardApprovalProcessConstants.ACC:
                this.populateAccLog();
                break;
            case BoardApprovalProcessConstants.COLLECTIONS:
                this.popupTitle = `Collections Referral Log: ${this.selectedRequest.processIdDisplayText}`;

                setTimeout(() => {
                    this.collectionLogUrl = `property/${this.selectedRequest.communityId}/${this.selectedRequest.customerId}/collection-board-history`;
                    this.showCollectionTransactionLog = true;
                }, 200);
                break;
            case BoardApprovalProcessConstants.WF:
                this.boardApprovalParam.communityId = this.selectedRequest.communityId;
                this.boardApprovalParam.workflowId = Number(this.selectedRequest.processId);
                this.showWorkFlowTransactionLog = true;
                break;
        }
    }

    onGridDetailsClick(record: BoardApprovalItemResultDto): void {
        this.selectedRequest = record;
        switch (this.selectedRequest.process) {
            case BoardApprovalProcessConstants.ACC:
                this.boardApprovalParam.communityId = this.selectedRequest.communityId;
                this.boardApprovalParam.accItemId = Number(this.selectedRequest.processId);
                this.boardApprovalParam.customerId = record.customerId;
                if (Utils.isTrue(this.selectedRequest.allowAction)) {
                    this.accEditorTitle = `Edit Architectural Application. Project ID: ${this.selectedRequest.processId}`;
                    this.showAccEditor = true;
                } else {
                    this.accEditorTitle = `Architectural Application. Project ID: ${this.selectedRequest.processId}`;
                    this.showAccDetail = true;
                }
                break;
            case BoardApprovalProcessConstants.COLLECTIONS:
                this.populateCollectionDetail();
                break;
            case BoardApprovalProcessConstants.WF:
                this.showWorkflowViewer = true;
                break;
        }
    }

    onActionClick(record: BoardApprovalItemResultDto): void {
        this.selectedRequest = record;
        this.showEditor = true;
    }

    onEditorClose(forceReload: boolean): void {
        this.showEditor = false;
        if (Utils.isTrue(forceReload)) {
            this.populateBoardApprovalQueue();
        }
    }

    onViewClick(record: BoardApprovalItemResultDto): void {
        this.selectedRequest = record;
        this.showEditor = true;
    }

    approvalRequireClick(process: string): void {
        const baseParams: BoardApprovalBaseParameterDto = new BoardApprovalBaseParameterDto();
        baseParams.communityId = this.queueParams.communityId;
        baseParams.clearCache = true;

        let service: Observable<any>;

        switch (process) {
            case BoardApprovalProcessConstants.ACC:
                service = this.sharedBoardApprovalService.getAccBoardRules(baseParams);
                break;
            case BoardApprovalProcessConstants.COLLECTIONS:
                service = this.sharedBoardApprovalService.getCollectionsBoardRules(baseParams);
                break;
            case BoardApprovalProcessConstants.WF:
                service = this.sharedBoardApprovalService.getWFBoardApprovalRules(baseParams);
                break;
        }
        if (Utils.notNullAndDefined(service)) {
            this.getBoardApprovalRule(service);
        }
    }

    onWorkflowAttachmentViewerClose(): void {
        this.showWorkflowAttachment = false;
    }

    onAccEditorClose(forceReload: boolean): void {
        this.showAccEditor = false;
        if (Utils.isTrue(forceReload)) {
            this.populateBoardApprovalQueue();
        }
    }

    onAccAttachmentEditorClose(flag: boolean): void {
        this.showAccAttachments = false;
        if (Utils.isTrue(flag)) {
            this.populateBoardApprovalQueue();
        }
    }

    onGridRowPrepared(event: any): void {
        const rec: BoardApprovalItemResultDto = event.data;
        if (Utils.equalsIgnoreCase(event.rowType, 'data') && Utils.notNullAndDefined(rec)) {
            /**
             * Logic for row color
             * If allowAction is false, set the row text to grey one.
             * If allowAction is false and isFinalStatus flag value is true, set the row color to highlight green
             */
            if (Utils.isFalse(rec.allowAction)) {
                event.rowElement.classList.add('cc-text-grey');

                if (Utils.isTrue(rec.isFinalStatus)) {
                    event.rowElement.classList.add('cc-row-highlight');
                }
            }
        }
    }

    /**
     * Method to set the loader message while loading board approval queue
     */
    private setBoardApprovalQueueLoadingMessage(): void {
        const selectedContext = this.contextManagerService.getSelectedContext();
        if (Utils.isNullOrUndefined(selectedContext)) {
            // Portfolio Context
            this.boardApprovalQueueLoadingMessage =
                'Loading Board Approval Queue for your Portfolio. It may take sometime, Please wait...';
        } else {
            // Community Context
            this.boardApprovalQueueLoadingMessage = 'Loading...';
        }
    }

    private populateWorkflowDocuments(): void {
        const baseParams: BoardApprovalWFParameterDto = new BoardApprovalWFParameterDto();
        baseParams.communityId = this.selectedRequest.communityId;
        baseParams.workFlowId = Number(this.selectedRequest.processId);

        this.boardApprovalHelperService.showLoading();
        this.sharedBoardApprovalService.getWorkflowDocument(baseParams).subscribe(
            (result: FileInfoDisplayResultDto) => {
                this.workFlowAttachment = result;
                this.boardApprovalHelperService.hideLoading();
                this.showWorkflowAttachment = true;
            },
            (error) => {
                this.boardApprovalHelperService.hideLoading();
                this.errorService.displayError(error);
            }
        );
    }

    private getBoardApprovalRule(service: any): void {
        this.boardApprovalHelperService.showLoading();
        service.subscribe(
            (result: BoardApprovalRulesResultDto) => {
                this.boardApprovalRules = result.rules;
                this.showBoardApprovalPopup = true;
                this.boardApprovalHelperService.hideLoading();
            },
            (error) => {
                this.boardApprovalHelperService.hideLoading();
                this.errorService.displayError(error);
            }
        );
    }

    private populateAccLog(): void {
        const param: AccBaseParameterDto = new AccBaseParameterDto();
        param.communityId = this.selectedRequest.communityId;
        param.customerId = this.selectedRequest.customerId;
        param.accItemId = Number(this.selectedRequest.processId);
        param.clearCache = true;
        this.popupTitle = `Log of ${this.selectedRequest.processId}`;
        this.boardApprovalHelperService.showLoading();
        this.accApplicationService.getAccItemHistoryLog(param).subscribe(
            (result: AccHistoryResultDto[]) => {
                this.transactionLogResultData = result;
                this.showAccTransactionLog = true;
                this.boardApprovalHelperService.hideLoading();
            },
            (error) => {
                this.boardApprovalHelperService.hideLoading();
                this.errorService.displayError(error);
            }
        );
    }

    private populateBoardApprovalQueue(): void {
        // Show loading with message
        this.boardApprovalHelperService.showLoading(this.boardApprovalQueueLoadingMessage);

        this.boardApprovalService.getBoardApprovalQueue(this.queueParams).subscribe(
            (result: BoardApprovalQueueResultDto) => {
                this.processResult = result.boardApprovalProcesses;
                let gridSource = result.boardRequests;
                gridSource.forEach((rec) => {
                    const process = this.processResult.find((pr) =>
                        Utils.equalsIgnoreCase(pr.processCode, rec.process)
                    );
                    rec.allowSelection = true;
                    rec.processDisplayText = Utils.notNullAndDefined(process) ? process.processDisplayText : null;
                });
                if (Utils.notNullAndDefined(this.queueParams.itemId)) {
                    gridSource = gridSource.filter((item) =>
                        Utils.equals(Number(item.processId), this.queueParams.itemId)
                    );
                }
                this.gridSource = gridSource;
                this.boardApprovalHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.boardApprovalHelperService.hideLoading();
            }
        );
    }

    private getSelectedProcess(code: string): BoardApprovalProcessesResultDto {
        return this.processResult.find((d) => Utils.equalsIgnoreCase(d.processCode, code));
    }

    private populateCollectionDetail(): void {
        const param: BoardApprovalDetailParameterDto = new BoardApprovalDetailParameterDto();
        param.communityId = this.selectedRequest.communityId;
        param.processId = this.selectedRequest.processId;
        this.boardApprovalHelperService.showLoading();
        this.sharedBoardApprovalService.getCollectionsDetail(param).subscribe(
            (result: BoardApprovalCollectionReferralResultDto) => {
                this.collectionsDetail = result;
                this.showCollectionReferral = true;
                this.boardApprovalHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.boardApprovalHelperService.hideLoading();
            }
        );
    }

    public resetGridSelection(): void {
        this.boardApprovalGrid.instance.clearSelection();
    }

    public reloadBoardApprovalQueue(): void {
        this.populateBoardApprovalQueue();
    }
}
